import { boxLineTimes } from '../../utils/boxLinePoints';
// frontend/src/components/charts/FuturesChartAdmin.js
import { useState, useEffect, useRef, useMemo } from 'react';
import { formatExitReason } from '../../utils/exitReasonLabel';
import {
  boxLineColor,
  boxLineStroke,
  boxLineActive,
} from '../../utils/boxLineStyle';
import { remainingAfterExit } from '../../utils/autoTradingExits';
import Highcharts, { color } from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import StockTools from 'highcharts/modules/stock-tools';
import PriceIndicator from 'highcharts/modules/price-indicator';
import AnnotationsAdvanced from 'highcharts/modules/annotations-advanced';
import FullScreen from 'highcharts/modules/full-screen';
import Accessibility from 'highcharts/modules/accessibility';
import axios from 'axios';
import CalendarDisplay from '../calendars/CalendarDisplay';
import FuturesInfo from '../common/FuturesInfo';
import MemoPopup from '../common/MemoPopup';
import AutoTradingStatsPanel from '../pages/AutoTradingStatsPanel';
import AutoTradingDetailHistoryPage from '../pages/AutoTradingDetailHistoryPage';
import { duration } from 'moment';

// 모듈 초기화 (내보내기가 default인지 아닌지 조건 처리)
if (typeof StockTools === 'function') {
  StockTools(Highcharts);
}
if (typeof AnnotationsAdvanced === 'function') {
  AnnotationsAdvanced(Highcharts);
}
if (typeof PriceIndicator === 'function') {
  PriceIndicator(Highcharts);
}
if (typeof FullScreen === 'function') {
  FullScreen(Highcharts);
}
if (typeof Accessibility === 'function') {
  Accessibility(Highcharts);
}

// 평일, 주말 판단 함수
function isNowWeekend(d) {
  const date = d instanceof Date ? d : new Date(d);
  const dow = date.getDay(); // 0:Sun, 1:Mon, …, 6:Sat

  return dow === 0 || dow === 6;
}
// 초기 로딩 데이터 범위 설정 - 평일: 과거 24시간, 주말: 과거 48시간
const INITIAL_HOURS_RANGE_FETCH = isNowWeekend(Date.now()) ? 48 : 24;

// 라운드피겨 박스라인 설정
const ROUND_FIGURE_BOX_LINES_VALUE = [
  10000, 11000, 12000, 13000, 14000, 15000, 16000, 17000, 18000, 19000, 20000,
  21000, 22000, 23000, 24000, 25000, 26000, 27000, 28000, 29000, 30000, 31000,
  32000, 33000, 34000, 35000, 36000, 37000, 38000, 39000, 40000,
];

// 청산 사유 한글 매핑
const EXIT_REASON_LABELS = {
  stop_loss: '손절',
  break_even: '본청',
  take_profit_30: '30p분할청산',
  take_profit_45: '45p분할청산',
  power_50: '강도50%청산',
  power_zero: '강도 0 청산 ',
  market_end: '장마감 청산',
  power_80: '강도80%청산',
  power_85: '강도85%청산',
  power_90: '강도90%청산',
  premarket_profit_over_100p: '프리장100p익절',
  price_over_25p_during_auto_trading_blocked: '25p이탈 진입차단',
};

//자동거래 라벨 괴거 n일치 까지 보이도록 설정
const OFFSET_SESSION_DAYS = isNowWeekend() ? 5 : 3;

// close 위에 두고 싶은 0선-간격(픽셀)
const ZERO_GAP_PX = 48;

// 서머타임 여부 확인: true (CDT, Daylight Time(서머타임)), false (CST, Standard Time)
const IS_DST = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Chicago',
  timeZoneName: 'short',
})
  .formatToParts(new Date())
  .some((p) => p.value === 'CDT');

export default function FuturesChartAdmin({ visiblePlotLines, onToggleLine }) {
  /* --------------------------------------------- */
  /* -------------- { 상태 관리 파트 } -------------- */
  /* --------------------------------------------- */

  const [chartOptions, setChartOptions] = useState({});
  const [isPowerRecording, setIsPowerRecording] = useState(false);
  const [powerStartTime, setPowerStartTime] = useState(null);
  const [currentCumulativePower, setCurrentCumulativePower] = useState(0);
  const [highestVolume, setHighestVolume] = useState([]);
  const [powerMinMax, setPowerMinMax] = useState({ minPower: 0, maxPower: 0 });
  const [latestClose, setLatestClose] = useState(0);
  const [boxLines, setBoxLines] = useState([]);
  const [isBoxLines, setIsBoxLines] = useState([]);
  const [loadingPrev, setLoadingPrev] = useState(false);
  const [customStartArmed, setCustomStartArmed] = useState(false); // Custom START 버튼 선택 상태
  const [customRangeArmed, setCustomRangeArmed] = useState(false); // Custom START 'Range' 버튼 선택 상태
  const [rangeSelectPhase, setRangeSelectPhase] = useState('idle'); // 'idle', 'start', 'end'
  const [tradeLabels, setTradeLabels] = useState([]); // annotation.labels

  const chartRef = useRef(null);
  const cumulativePowerRef = useRef(0); // 누적 파워와 마지막 처리한 timestamp를 위한 ref
  const lastTimestampRef = useRef(0); // 마지막으로 받아온 ohlcp의 가장 최근의 timestamp 저장
  const firstTimestampRef = useRef(0); // 마지막으로 받아온 ohlcp의 가장 맨 앞의 timestamp 저장
  const lastPowerTimestampRef = useRef(0); // 마지막으로 처리한 DB 데이터의 timestamp
  const earliestTsRef = useRef(null); // 과거 차트 데이터 로드 기능을 위한 Ref. 초기 로딩이 끝난 직후 가장 앞쪽 데이터의 시각을 저장
  const isBoxLineRef = useRef(isBoxLines); // 최신 is_boxline 목록
  const lastAutoStartRef = useRef(0); // 직전 Auto-Start 시각(ms)
  const lastCloseRef = useRef(null); // 직전 close 가격
  const loadCountRef = useRef(0); // 과거 데이터 로드 최대 횟수 제한을 위한 Ref
  const tradeLabelsPollId = useRef(null); // trade labels 폴링 타이머
  const tradeLabelsBusyRef = useRef(false); // 중복 호출 방지 플래그

  // custom start 관련 Ref
  const customStartArmedRef = useRef(customStartArmed);
  const customRangeArmedRef = useRef(false);
  const rangeStartXRef = useRef(null);
  const rangeEndXRef = useRef(null);
  const rangeMarkersRef = useRef({
    startLineId: null,
    endLineId: null,
    bandId: null,
  }); // 플롯 라인/밴드 ID 기억용
  const rangePhaseRef = useRef('idle');

  // interval 관리용 ref
  const latestId = useRef();
  const boxId = useRef();
  const volumeId = useRef();
  const isBoxlineId = useRef();
  const pollingTimerRef = useRef(null); // 실시간 setInterval ID

  // 최신 state를 ref에 동기화 (stale closure 방지)
  const isPowerRecRef = useRef(isPowerRecording);
  const powerStartRef = useRef(powerStartTime);
  const powerMinMaxRef = useRef(powerMinMax);

  useEffect(() => {
    isPowerRecRef.current = isPowerRecording;
  }, [isPowerRecording]);
  useEffect(() => {
    powerStartRef.current = powerStartTime;
  }, [powerStartTime]);
  useEffect(() => {
    powerMinMaxRef.current = powerMinMax;
  }, [powerMinMax]);
  useEffect(() => {
    isBoxLineRef.current = isBoxLines;
  }, [isBoxLines]); // is_boxline 목록 최신화 -> ref 동기화 (stale closure 방지)
  useEffect(() => {
    customStartArmedRef.current = customStartArmed;
  }, [customStartArmed]);
  useEffect(() => {
    customRangeArmedRef.current = customRangeArmed;
  }, [customRangeArmed]);
  useEffect(() => {
    rangePhaseRef.current = rangeSelectPhase;
  }, [rangeSelectPhase]);

  /* ------------------------------------------- */
  /* ------------- { 유틸/헬퍼 } ------------- */
  /* ------------------------------------------- */

  // 서버가 tz-aware(+09:00) ISO를 기대하므로 KST 고정 ISO 생성
  function getKSTIsoString(date) {
    const formatted = new Intl.DateTimeFormat('sv-SE', {
      timeZone: 'Asia/Seoul',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(date); // "2025-04-17 07:00:00"
    return formatted.replace(' ', 'T') + '+09:00';
  }

  const fmt2 = (n) =>
    n == null
      ? ''
      : Number(n).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

  const mapExitReason = (reason, event) =>
    formatExitReason(reason, event, EXIT_REASON_LABELS);

  function getSessionStartMsKST(offsetSessions = 0, now = new Date()) {
    const nowKST = new Date(
      now.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }),
    );
    const start = new Date(nowKST);

    if (IS_DST) {
      // 서머타임인 경우
      if (nowKST.getHours() >= 7) {
        start.setHours(7, 0, 0, 0); // 오늘 07:00
      } else {
        start.setDate(start.getDate() - 1);
        start.setHours(7, 0, 0, 0); // 어제 07:00
      }
    } else {
      // 비 서머타임인 경우
      if (nowKST.getHours() >= 8) {
        start.setHours(8, 0, 0, 0); // 오늘 08:00
      } else {
        start.setDate(start.getDate() - 1);
        start.setHours(8, 0, 0, 0); // 어제 08:00
      }
    }

    // 세션 오프셋 적용: -1 => 어제 세션 시작, -2 => 그저께 세션 시작 …
    return start.getTime() + offsetSessions * 24 * 60 * 60 * 1000;
  }

  // object/boolean 혼용 visiblePlotLines에서 "체크 여부"를 안전하게 뽑는 헬퍼
  function isLineChecked(val) {
    if (typeof val === 'object' && val !== null) {
      return !!val.checked;
    }
    return !!val;
  }

  /* ------------------------------------------- */
  /* ---------- { 자동거래 라벨 표시 파트 } ---------- */
  /* ------------------------------------------- */

  // history-range API로 구간 내 전략을 받아 라벨 배열 생성
  async function fetchTradeLabelsForRange(startMs, endMs) {
    try {
      const startMsSafe = Number.isFinite(startMs)
        ? startMs
        : getSessionStartMsKST(-OFFSET_SESSION_DAYS - loadCountRef.current);
      const endMsSafe = Number.isFinite(endMs)
        ? endMs
        : lastTimestampRef.current
          ? new Date(lastTimestampRef.current).getTime()
          : Date.now();

      const startIso = getKSTIsoString(new Date(startMsSafe));
      const endIso = getKSTIsoString(new Date(endMsSafe));

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/auto-trading/strategies/history-range`,
        {
          params: {
            start: startIso,
            end: endIso,
            include_open_spanning: true, // 구간을 걸치는 열린 포지션 포함
          },
        },
      );
      const strategies = res.data ?? [];

      // events_history에서 enter/exit 추출 → 라벨 포인트 생성
      const labels = [];
      for (const s of strategies) {
        const directionLong = s.direction === 'long' || s.direction === true;
        const dirLabel = directionLong ? '매수' : '매도';
        const evts = Array.isArray(s.events_history)
          ? [...s.events_history]
          : [];
        evts.sort((a, b) => new Date(a.t) - new Date(b.t));

        let open = null;
        for (const e of evts) {
          if (e.type === 'enter') {
            open = {
              t: e.t,
              price: Number(e.price),
              open_qty: Number(e.qty ?? 1),
            };
            // 진입 라벨
            labels.push({
              point: {
                xAxis: 0,
                yAxis: 0,
                x: new Date(open.t).getTime(),
                y: open.price,
              },
              text: `진입(${dirLabel})\n${fmt2(open.price)}`,
              backgroundColor: directionLong
                ? 'rgba(230, 56, 56, 0.341)'
                : 'rgba(37, 100, 235, 0.337)',
              borderColor: directionLong ? '#dc2626' : '#2563eb',
              borderWidth: 1,
              shape: 'callout',
              style: {
                fontSize: '10px',
                fontWeight: '600',
                color: 'black',
              },
              // 약간 위/아래로 오프셋
              // y: directionLong ? -10 : 10,
              verticalAlign: 'bottom',
              includeInDataExport: false,
            });
          } else if (e.type === 'exit' && open) {
            const exitPrice =
              e.price != null
                ? Number(e.price)
                : s.exit_price != null
                  ? Number(s.exit_price)
                  : null;
            const profit =
              typeof e.profit === 'number'
                ? e.profit
                : exitPrice != null
                  ? (exitPrice - open.price) * (directionLong ? 1 : -1)
                  : null;
            const profitTxt =
              profit == null ? '' : `\nP/L ${profit.toFixed(2)}p`;
            const reasonTxt = e.reason || s.exit_reason || '';
            labels.push({
              point: {
                xAxis: 0,
                yAxis: 0,
                x: new Date(e.t).getTime(),
                y: exitPrice ?? open.price,
              },
              text: `${mapExitReason(reasonTxt, e)}\n${fmt2(exitPrice ?? '')}`,
              backgroundColor:
                profit != null
                  ? profit >= 0
                    ? 'rgba(16,185,129,0.14)'
                    : 'rgba(239,68,68,0.14)'
                  : 'rgba(107,114,128,0.14)',
              borderColor:
                profit != null
                  ? profit >= 0
                    ? '#10b981'
                    : '#ef4444'
                  : '#6b7280',
              borderWidth: 1,
              shape: 'callout',
              style: { fontSize: '10px', fontWeight: '600', color: 'black' },
              y: profit != null ? (profit >= 0 ? -10 : 10) : 0,
              verticalAlign: 'bottom',
              includeInDataExport: false,
            });
            const remainingQty = remainingAfterExit(open, e, e.qty);
            open =
              remainingQty > 0 ? { ...open, open_qty: remainingQty } : null;
          }
        }

        // 아직 열린 포지션(enter만 있고 exit 없음) — “LIVE” 표기
        // if (open) {
        //   labels.push({
        //     point: {
        //       xAxis: 0,
        //       yAxis: 0,
        //       x: new Date(open.t).getTime(),
        //       y: open.price,
        //     },
        //     text: `진입(${dirLabel})\n${fmt2(open.price)}\nLIVE`,
        //     backgroundColor: 'rgba(234,179,8,0.16)',
        //     borderColor: '#eab308',
        //     borderWidth: 1,
        //     shape: 'callout',
        //     style: { fontSize: '10px', fontWeight: '700' },
        //     y: -10,
        //   });
        // }
      }

      setTradeLabels(labels);
    } catch (e) {
      console.error('자동거래 라벨 조회 실패:', e);
      setTradeLabels([]);
    }
  }

  /* ------------------------------------------- */
  /* ------------- { 함수 정의 파트 } ------------- */
  /* ------------------------------------------- */

  function startIntervals() {
    latestId.current = setInterval(fetchLatest, 1_000);
    boxId.current = setInterval(fetchBoxLines, 10_000);
    isBoxlineId.current = setInterval(fetchAllIsBoxlines, 10_000);
    volumeId.current = setInterval(fetchHighestVolume, 10_000);
  }

  async function fetchCatchUp() {
    if (!lastTimestampRef.current) return;

    const gapMs = Date.now() - Date.parse(lastTimestampRef.current);
    // 30 분(1,800,000 ms) 이상 비어 있으면 배치 호출
    if (gapMs < 1800_000) return;

    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/data/ls_nasdaq_ohlcp/range`,
        {
          params: { start: lastTimestampRef.current },
        },
      );

      const priceSeries = chartRef.current.chart.series[0];
      const newData = data.map((d) => [
        Date.parse(d.timestamp_korean),
        +d.open,
        +d.high,
        +d.low,
        +d.close,
      ]);
      priceSeries.setData(newData, false); // 한 번에 넣기
      chartRef.current.chart.redraw(false);

      // 가장 마지막 timestamp 저장
      if (data.length) lastTimestampRef.current = data.at(-1).timestamp_korean;
    } catch (e) {
      console.error('catch-up 실패:', e);
    }
  }

  function clearAllIntervals() {
    [latestId, boxId, isBoxlineId, volumeId].forEach((r) => {
      if (r?.current) clearInterval(r.current);
    });
  }

  // page visibility, 인터벌 관리 useEffect
  useEffect(() => {
    startIntervals(); // 마운트 시 폴링 인터벌 한 번만 시작

    // 숨겨졌다 돌아올 때는 잔여 데이터 보충만
    const onVisible = () => {
      if (!document.hidden) {
        fetchCatchUp();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearAllIntervals(); // 언마운트 시만 인터벌 해제
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []); // 최초 1회만

  // 매초 새 데이터 폴링: /data/ls_nasdaq_ohlcp/latest
  const fetchLatest = async () => {
    if (!lastTimestampRef.current || !chartRef.current?.chart) return;
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/data/ls_nasdaq_ohlcp/latest`,
        {
          params: { since: lastTimestampRef.current },
        },
      );
      const arr = Array.isArray(data) ? data : [data];
      if (!arr.length) return;
      const chart = chartRef.current.chart;
      const priceSeries = chart.series[0];
      const powerSeries = chart.series[1];
      arr.forEach((item) => {
        const x = new Date(item.timestamp_korean).getTime();
        const y = parseFloat(item.close);
        const open = parseFloat(item.open);
        const high = parseFloat(item.high);
        const low = parseFloat(item.low);
        const close = parseFloat(item.close);
        const last = priceSeries.data.at(-1);
        if (!last || x > last.x)
          priceSeries.addPoint(
            [x, open, high, low, close],
            false,
            priceSeries.data.length > 36000,
          );

        // 누적 파워
        if (
          isPowerRecRef.current &&
          powerStartRef.current != null &&
          x >= powerStartRef.current &&
          x > lastPowerTimestampRef.current
        ) {
          lastPowerTimestampRef.current = x;
          cumulativePowerRef.current += parseInt(item.power) || 0;
          powerSeries.addPoint([x, cumulativePowerRef.current], true, false);
          setCurrentCumulativePower(cumulativePowerRef.current);
          const mm = {
            minPower: Math.min(
              powerMinMaxRef.current.minPower,
              cumulativePowerRef.current,
            ),
            maxPower: Math.max(
              powerMinMaxRef.current.maxPower,
              cumulativePowerRef.current,
            ),
          };
          powerMinMaxRef.current = mm;
          setPowerMinMax(mm);

          const range = Math.max(
            Math.abs(mm.maxPower),
            Math.abs(mm.minPower),
            50,
          );
          chart.yAxis[1].update({
            min: -range,
            max: range,
          });
        }
        setLatestClose(y);
        lastTimestampRef.current = item.timestamp_korean;

        // 자동 강도 기록 트리거
        const prevClose = lastCloseRef.current;
        if (crossedBoxLine(prevClose, close)) {
          if (!isPowerRecording) autoStartPower(x); // 강도 기록 중이어도 항상 재시작
        }
        lastCloseRef.current = close; // 직전 가격 갱신
      });
      chart.redraw(true);
    } catch (e) {
      console.error('latest fetch 오류:', e);
    }
  };

  // boxLines 데이터 가져오기
  const fetchBoxLines = async () => {
    await axios
      .get(`${process.env.REACT_APP_API_URL}/boxlines/get_box_lines`, {
        params: {
          days: 7,
        },
      })
      .then((res) => {
        setBoxLines(res.data);
      })
      .catch((err) => {
        console.error('박스 라인 데이터 fetch 에러:', err);
      });
  };

  const fetchAllIsBoxlines = async () => {
    await axios
      .get(
        `${process.env.REACT_APP_API_URL}/boxlines/get_all_is_box_line_range`,
        {
          params: {
            days: 7,
          },
        },
      )
      .then((res) => {
        setIsBoxLines(res.data);
      })
      .catch((err) => {
        console.error('fetchAllIsBoxlines fetch 에러:', err);
      });
  };

  // highestVolume 데이터 가져오기
  const fetchHighestVolume = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/data/ls_nasdaq_ohlcp/tables/top_volume_by_interval`,
      );
      // 백엔드에서 {"0-1h": [...], "1-2h": [...], ... } 형태
      setHighestVolume(response.data);

      // 모든 각 구간의 각 row에 대해 composite key 설정
      Object.keys(response.data).forEach((interval_label) => {
        response.data[interval_label].forEach((row) => {
          const key = `${interval_label}_${row.rank}`;

          // 초기에는 rank 1~5는 true, rank 6 이상은 false
          if (visiblePlotLines[key] === undefined) {
            onToggleLine(key, row.rank <= 5, {
              interval: interval_label,
              rank: row.rank,
              timestamp_korean: row.timestamp_korean,
              close: row.close,
              volume: row.volume,
              power: row.power,
            });
          }
        });
      });
    } catch (error) {
      console.error('highestVolume data fetch error:', error);
      setHighestVolume([]);
    }
  };

  /* ----------------------------------------------- */
  /* ------------- { 강도 기록 기능 관련 } ------------- */
  /* ----------------------------------------------- */

  // 차트 데이터에서 가장 최근의 (x, close) 찾기
  function getLastPointInfoSafe() {
    const chart = chartRef.current?.chart;
    const s = chart?.series?.[0];
    if (!chart || !s) return null;

    // 1) 화면에 렌더된 points 우선 (null 포인트 제외)
    const pts = s.points;
    if (pts?.length) {
      for (let i = pts.length - 1; i >= 0; i--) {
        const p = pts[i];
        if (!p) continue;
        const close = p.close ?? p.y;
        if (p.x != null && close != null) return { x: p.x, close };
      }
    }

    // 2) 원 데이터(Highcharts Point 또는 원시 배열)에서 역방향 스캔
    const data = s.data;
    if (data?.length) {
      for (let i = data.length - 1; i >= 0; i--) {
        const d = data[i];
        if (!d) continue;
        const x = d.x ?? (Array.isArray(d) ? d[0] : undefined);
        const close = d.close ?? d.y ?? (Array.isArray(d) ? d[4] : undefined);
        if (x != null && close != null) return { x, close };
      }
    }

    // 3) 최소한 x만이라도 확보
    const xData = s.xData;
    if (xData?.length) return { x: xData[xData.length - 1], close: null };

    return null;
  }

  // 최근 종가의 플롯영역 기준 y 상대위치 (0~1, 위=0, 아래=1)
  function getCloseRelYRaw(close) {
    const chart = chartRef.current?.chart;
    if (!chart || close == null) return null;

    const yPx = chart.yAxis[0].toPixels(close); // 컨테이너 기준
    const yPlot = yPx - chart.plotTop; // 플롯 기준(px)
    if (!Number.isFinite(yPlot) || chart.plotHeight <= 0) return null;

    let rel = yPlot / chart.plotHeight; // 0~1
    return Math.max(0, Math.min(1, rel));
  }

  // yAxis[1]의 height를 %로 가져오되, px이면 plotHeight 기준으로 %로 환산
  function getYAxis1HeightPct(chart) {
    const y1 = chart.yAxis?.[1];
    if (!y1) return 30; // 기본값 방어
    const h = y1.options?.height ?? '30%';
    if (typeof h === 'string' && h.endsWith('%')) {
      const v = parseFloat(h);
      return Number.isFinite(v) ? v : 30;
    }
    if (typeof h === 'number' && chart.plotHeight > 0) {
      return (h / chart.plotHeight) * 100;
    }
    return 30;
  }

  // START 버튼: 현재 가격 데이터의 마지막 시점 기준으로 power 기록 시작
  const handleStartPower = () => {
    const chart = chartRef.current?.chart;
    const s0 = chart?.series?.[0];
    if (!chart || !s0) {
      console.error('차트 혹은 데이터가 준비되지 않았습니다.');
      return;
    }

    // 안전하게 마지막 x/close 확보
    const info = getLastPointInfoSafe();

    // x가 없으면 xAxis 범위/max 또는 현재시각으로 대체
    const startX =
      info?.x ?? chart.xAxis?.[0]?.max ?? chart.xAxis?.[0]?.min ?? Date.now();

    // 강도 기록 차트 초기화
    setPowerStartTime(startX);
    cumulativePowerRef.current = 0;
    setCurrentCumulativePower(0);
    lastPowerTimestampRef.current = 0;
    setPowerMinMax({ minPower: 0, maxPower: 0 });
    if (chart.series[1]) chart.series[1].setData([]);

    // 0기준선 plotLines 설정
    chart.yAxis[1].update(
      {
        plotLines: [
          {
            color: 'black',
            value: 0,
            width: 1,
            zIndex: 5,
            dashStyle: 'dash',
            labels: true,
            label: { text: '0', align: 'right', style: { color: 'grey' } },
          },
        ],
      },
      false,
    );

    // 오버레이 배치: yAxis[1]만 top 조정 (0선이 close보다 ZERO_GAP_PX 위에 오게)
    const rel = getCloseRelYRaw(info?.close); // 0(상단)~1(하단)
    if (rel != null) {
      const y1 = chart.yAxis[1];

      // 현재 보조축 높이(%). 초기 '30%'면 30이 나옴
      const heightPct = getYAxis1HeightPct(chart);

      // 원하는 "중앙 위치"(= 0선 위치)를 close보다 GAP만큼 위로
      // close의 플롯 상대 위치(%)
      const closePct = rel * 100;
      // GAP(px) → GAP(%)
      const gapPct = (ZERO_GAP_PX / chart.plotHeight) * 100;

      // 중앙(=0선)이 있어야 할 목표 위치(%)
      let centerPct = closePct - gapPct;

      // top = 중앙 - height/2
      let topPct = centerPct - heightPct / 2;

      // 플롯 밖으로 못 나가게 클램프
      const maxTop = 100 - heightPct;
      if (!Number.isFinite(topPct)) topPct = 0;
      topPct = Math.max(0, Math.min(maxTop, topPct));

      y1.update({ top: topPct.toFixed(4) + '%' }, false);
    }

    chart.redraw();
    setIsPowerRecording(true);
  };

  // RESET 버튼: power 기록 중지
  const handleResetPower = () => {
    const chart = chartRef.current?.chart;
    // 초기화
    setIsPowerRecording(false);
    cumulativePowerRef.current = 0;
    setCurrentCumulativePower(0);
    lastPowerTimestampRef.current = 0;
    clearRangeMarkers();
    setPowerMinMax({ minPower: 0, maxPower: 0 });
    if (chart.series[1]) chart.series[1].setData([]);
    chart.yAxis[1].update(
      {
        plotLines: [],
      },
      false,
    );
  };

  // 가격이 most_line = true인 자동거래 진입라인을 터치했는지 방향을 고려해 crossing 판정
  const crossedBoxLine = (prev, curr) => {
    if (prev === null) return false;
    const EPS = 0.001; // tick=0.25, 부동소수점 오차 보정

    return isBoxLineRef.current.some((line) => {
      if (!line.most_line) return false;
      const box = line.close_box;
      // Case 1: 아래 -> 위, Case 2: 위 -> 아래
      const crossedUp = prev < box - EPS && curr >= box - EPS;
      const crossedDown = prev > box + EPS && curr <= box + EPS;

      // 동일 선을 다시 터치하는 것도 허용하려면 line위에 있던 상태에서 살짝 떨어졌다가 다시 터치
      // 하는 경우도 포함시켜야 하지만, 1초 쿨타임(lastAutoStartRef)으로 충분히 처리 가능
      return crossedUp || crossedDown;
    });
  };

  // Power 차트 초기화 + 즉시 START(수동 START와 동일한 내부 동작)
  const autoStartPower = (startX) => {
    if (!chartRef.current?.chart) return;
    const chart = chartRef.current.chart;

    // 중복 트리거 최소화: 1초 이내엔 무시
    if (Date.now() - lastAutoStartRef.current < 1000) return;
    lastAutoStartRef.current = Date.now();

    // 그래프, 관련 state 리셋
    cumulativePowerRef.current = 0;
    lastPowerTimestampRef.current = 0;
    setCurrentCumulativePower(0);
    setPowerMinMax({ minPower: 0, maxPower: 0 });
    if (chart.series[1]) chart.series[1].setData([]);

    // START
    setPowerStartTime(startX);
    setIsPowerRecording(true);

    chart.yAxis[1].update(
      {
        plotLines: [
          {
            color: 'black',
            value: 0,
            width: 1,
            zIndex: 5,
            dashStyle: 'dash',
            labels: true,
            label: { text: '0', align: 'right', style: { color: 'grey' } },
          },
        ],
      },
      false,
    );
  };

  /* ----- Custom Start 기능 관련----- */

  // 헬퍼
  function ensureZeroPlotLine(chart) {
    chart.yAxis[1].update(
      {
        plotLines: [
          {
            color: 'black',
            value: 0,
            width: 1,
            zIndex: 5,
            dashStyle: 'dash',
            label: { text: '0', align: 'right', style: { color: 'grey' } },
          },
        ],
      },
      false,
    );
  }

  function positionYAxis1ZeroNearClose(closeCandidate) {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    // closeCandidate가 없으면 차트의 가장 최근 close를 얻어 시도
    let close = closeCandidate;
    if (close == null) {
      const s0 = chart.series?.[0];
      const last = s0?.points?.at(-1) ?? s0?.data?.at(-1);
      close = last?.close ?? last?.y ?? (Array.isArray(last) ? last[4] : null);
    }

    const rel = getCloseRelYRaw(close);
    if (rel == null) return;

    const y1 = chart.yAxis[1];
    const heightPct = getYAxis1HeightPct(chart);
    const closePct = rel * 100;
    const gapPct = (ZERO_GAP_PX / chart.plotHeight) * 100;

    let centerPct = closePct - gapPct; // 0선이 있어야 할 위치(%)
    let topPct = centerPct - heightPct / 2; // 보조축 top(%)

    const maxTop = 100 - heightPct;
    if (!Number.isFinite(topPct)) topPct = 0;
    topPct = Math.max(0, Math.min(maxTop, topPct));

    y1.update({ top: topPct.toFixed(4) + '%' }, false);
  }

  // 구간 마커 정리
  function clearRangeMarkers() {
    const chart = chartRef.current?.chart;
    if (!chart) return;
    const xa = chart.xAxis?.[0];
    if (!xa) return;

    const { startLineId, endLineId, bandId } = rangeMarkersRef.current;
    if (startLineId) xa.removePlotLine(startLineId);
    if (endLineId) xa.removePlotLine(endLineId);
    if (bandId) xa.removePlotBand(bandId);

    rangeMarkersRef.current = {
      startLineId: null,
      endLineId: null,
      bandId: null,
    };
    rangeStartXRef.current = null;
    rangeEndXRef.current = null;
  }

  // custom start 기능
  async function prefillPowerFrom(startXms) {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    // 강도 차트 관련 초기화
    if (chart.series[1]) chart.series[1].setData([], false);
    ensureZeroPlotLine(chart);
    positionYAxis1ZeroNearClose(lastCloseRef.current);
    chart.redraw(false);

    // KST ISO로 변환하여 range API 호출
    const startIsoKst = getKSTIsoString(new Date(startXms));
    let rows = [];
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/data/ls_nasdaq_ohlcp/range`,
        {
          params: { start: startIsoKst },
        },
      );
      rows = Array.isArray(data) ? data : [];
    } catch (e) {
      console.error('custom prefill fetch 실패:', e);
      alert('데이터를 불러오지 못했습니다.');
      return;
    }
    if (!rows.length) {
      alert('해당 시점 이후 데이터가 없습니다.');
      return;
    }

    // 누적 파워 계산
    let cum = 0,
      minP = 0,
      maxP = 0;
    const powerSeriesData = rows
      .sort(
        (a, b) => new Date(a.timestamp_korean) - new Date(b.timestamp_korean),
      )
      .map((r) => {
        const x = new Date(r.timestamp_korean).getTime();
        const p = parseInt(r.power) || 0;
        cum += p;
        if (cum < minP) minP = cum;
        if (cum > maxP) maxP = cum;
        return [x, cum];
      });

    // 강도차트 y축 범위 업데이트
    const range = Math.max(Math.abs(minP), Math.abs(maxP), 50);
    chart.yAxis[1].update({ min: -range, max: range }, false);

    // 데이터 반영
    chart.series[1].setData(powerSeriesData, false);
    chart.redraw(true);

    // 누적 기록 이어가기 위한 내부 상태 동기화
    cumulativePowerRef.current = cum;
    setCurrentCumulativePower(cum);
    setPowerMinMax({ minPower: minP, maxPower: maxP });

    const lastX = powerSeriesData.at(-1)[0];
    lastPowerTimestampRef.current = lastX; // 여기까지는 이미 반영됨
    setPowerStartTime(startXms); // 기록 시작 시각 등록
    setIsPowerRecording(true); // ← 이제부터 실시간 addPoint 이어붙임
  }

  // custom start range 기능
  async function prefillPowerRange(startXms, endXms) {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    // 강도 차트 초기화
    ensureZeroPlotLine(chart);
    positionYAxis1ZeroNearClose(lastCloseRef.current);

    const startIsoKst = getKSTIsoString(new Date(startXms));
    const endIsoKst = getKSTIsoString(new Date(endXms));

    // 1) 우선 start ~ end 범위를 서버에서 바로 받는 시도
    let rows = [];
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/data/ls_nasdaq_ohlcp/range`,
        {
          params: { start: startIsoKst, end: endIsoKst },
        },
      );
      rows = Array.isArray(data) ? data : [];
    } catch (e) {
      // 2) 백엔드가 end 파라미터를 아직 지원하지 않을 경우: start ~ 현재까지 받아서 클라이언트에서 end로 필터
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/data/ls_nasdaq_ohlcp/range`,
          {
            params: { start: startIsoKst },
          },
        );
        rows = (Array.isArray(data) ? data : []).filter((r) => {
          const t = new Date(r.timestamp_korean).getTime();
          return t <= endXms;
        });
      } catch (e2) {
        console.error('custom range fetch 실패', e2);
        alert('데이터를 불러오지 못했습니다.');
        return;
      }
    }

    if (!rows.length) {
      alert('선택한 구간에 데이터가 없습니다.');
      return;
    }

    // 시간 정렬 및 누적 강도 계산(시작을 0 기준으로)
    rows.sort(
      (a, b) => new Date(a.timestamp_korean) - new Date(b.timestamp_korean),
    );

    let cum = 0,
      minP = 0,
      maxP = 0;
    const points = rows.map((r) => {
      const x = new Date(r.timestamp_korean).getTime();
      const p = parseInt(r.power) || 0; // 정수/실수 모두 처리
      cum += p;
      if (cum < minP) minP = cum;
      if (cum > maxP) maxP = cum;
      return [x, cum];
    });

    // 시리즈 존재 확인(없으면 생성)
    let powerSeries =
      chart.series.find((s) => s.userOptions?.id === 'powerSeries') ||
      chart.series?.[1];

    if (!powerSeries) {
      powerSeries = chart.addSeries(
        {
          id: 'powerSeries',
          name: 'Power (cum)',
          type: 'line',
          yAxis: 1,
          data: points,
          tooltip: { valueDecimals: 0 },
        },
        false,
      );
    } else {
      powerSeries.setData(points, false);
    }

    // 축 범위 대칭 설정
    const rangeAbs = Math.max(Math.abs(minP), Math.abs(maxP), 50);
    chart.yAxis[1].update({ min: -rangeAbs, max: rangeAbs }, false);

    chart.redraw(true);
  }

  // 차트 클릭 핸들러
  function onChartClick(e) {
    const chart = chartRef.current?.chart;
    const xaObj = e?.xAxis?.[0];
    if (!chart || !xaObj || xaObj.value == null) return;

    const clickX = Math.round(xaObj.value);
    const xAxis = chart.xAxis[0];

    // --- Custom START(단일) 모드 ---
    if (customStartArmedRef.current) {
      // 클릭과 동시에 무장 해제
      setCustomStartArmed(false);
      // (Range 마커가 남아 있다면 정리)
      clearRangeMarkers();

      // 클릭한 지점부터 현재까지 누적 강도 Prefill + 실시간 기록 시작
      prefillPowerFrom(clickX);
      return;
    }

    // --- Custom START Range 모드 ---
    if (customRangeArmedRef.current) {
      if (rangePhaseRef.current === 'start') {
        // 시작점 지정
        rangeStartXRef.current = clickX;

        // 시작 라인 표시
        const startId = 'customRangeStart';
        xAxis.removePlotLine(startId);
        xAxis.addPlotLine({
          id: startId,
          value: clickX,
          color: '#0d6efd',
          width: 1,
          zIndex: 5,
        });
        rangeMarkersRef.current.startLineId = startId;

        setRangeSelectPhase('end');
        return;
      }

      if (rangePhaseRef.current === 'end') {
        // 끝점 지정
        rangeEndXRef.current = clickX;

        // 끝 라인 표시
        const endId = 'customRangeEnd';
        xAxis.removePlotLine(endId);
        xAxis.addPlotLine({
          id: endId,
          value: clickX,
          color: '#dc3545',
          width: 1,
          zIndex: 5,
        });
        rangeMarkersRef.current.endLineId = endId;

        // from/to 정규화
        const s = Math.min(rangeStartXRef.current, clickX);
        const t = Math.max(rangeStartXRef.current, clickX);
        if (s === t) return; // 같은 점을 두 번 찍은 경우 방어

        // 밴드 표시
        const bandId = 'customRangeBand';
        xAxis.removePlotBand(bandId);
        xAxis.addPlotBand({
          id: bandId,
          from: s,
          to: t,
          color: 'rgba(13,110,253,0.08)',
          zIndex: 3,
        });
        rangeMarkersRef.current.bandId = bandId;

        // 무장해제 & 계산
        setCustomRangeArmed(false);
        setRangeSelectPhase('idle');

        prefillPowerRange(s, t);
        return;
      }
    }
  }

  /* --------------------------------------------------- */
  /* ---------------- plotLines 계산 관련 ---------------- */
  /* --------------------------------------------------- */

  // 1) most_line 파트
  const calMostPlotLines = () =>
    boxLines
      .filter((line) => line.most_line && !isNaN(line.close_box))
      .map((line, index) => {
        // 1) 최소 타임스탬프(ms)
        const minTsMs = Math.min(...boxLineTimes(line));

        // 2) KST 기준의 '연-월-일'만 떼어 날짜 차이 계산
        const toKSTDateOnly = (ms) => {
          const kst = new Date(
            new Date(ms).toLocaleString('en-US', { timeZone: 'Asia/Seoul' }),
          );
          // 자정(00:00:00)으로 내림
          return new Date(
            Date.UTC(kst.getFullYear(), kst.getMonth(), kst.getDate()),
          );
        };

        const todayKST = toKSTDateOnly(Date.now());
        const targetKST = toKSTDateOnly(minTsMs);

        const MS_PER_DAY = 24 * 60 * 60 * 1000; // 86,400,000ms
        const daysDelta = Math.round((todayKST - targetKST) / MS_PER_DAY); // 항상 캘린더 일수 차이

        return {
          id: `most-${index}`,
          value: +line.close_box,
          color: boxLineColor(line),
          width: boxLineActive(line) ? 2 : 1,
          dashStyle: boxLineActive(line) ? 'Solid' : 'Dash',
          label: {
            text: `${daysDelta}일전 - ${line.close_box}`,
            align: 'left',
          },
        };
      });

  // 2) top volume line 파트
  const calTopVolumePlotLines = () => {
    // 시간대별 색상 매핑
    const colorMapping = {
      1: '#FF0000', // 빨간색
      2: '#FFA500', // 주황색
      3: '#ffd907', // 노란색
      4: '#008000', // 녹색
      5: '#800080', // 보라색
      // 6: "#0000FF", // 파란색
      // 7: "#060c8d", // 남색
    };

    // 그룹별로 plotLine 객체 생성
    const newPlotLines = [];

    // highestVolume은 {"0-1h": [...], "1-2h": [...], ... } 형태
    Object.keys(highestVolume).forEach((interval_label) => {
      const rowArray = highestVolume[interval_label];
      // rowArray는 해당 interval_label의 배열
      rowArray.forEach((item, idx) => {
        // composite key 생성
        const toggleKey = `${interval_label}_${item.rank}`;
        // 체크 박스가 해제되어 있으면 표시하지 않음
        if (!isLineChecked(visiblePlotLines[toggleKey])) return;

        // const plotLinesLabelText =
        //   item.rank === 1 || item.rank === 2
        //     ? `${interval_label}(${item.rank}):${item.volume}`
        //     : '';

        // plotLines 생성
        const value = parseFloat(item.close) || 0;
        newPlotLines.push({
          value,
          color: colorMapping[item.rank] || 'gray',
          width: 2,
          // label: {
          //   text: plotLinesLabelText,
          //   align: 'right',
          //   x: 75,
          //   y: 10,
          //   style: { fontWeight: 'bold', fontSize: '15px', textAlign: 'start' },
          // },
        });
      });
    });

    return newPlotLines;
  };

  // 3) is_boxline 파트
  const calBoxPlotLines = () =>
    isBoxLines
      .filter((line) => line.is_boxline && !isNaN(line.close_box))
      .map((line, index) => {
        // 1) 최소 타임스탬프(ms)
        const minTsMs = Math.min(...boxLineTimes(line));

        // 2) KST 기준의 '연-월-일'만 떼어 날짜 차이 계산
        const toKSTDateOnly = (ms) => {
          const kst = new Date(
            new Date(ms).toLocaleString('en-US', { timeZone: 'Asia/Seoul' }),
          );
          // 자정(00:00:00)으로 내림
          return new Date(
            Date.UTC(kst.getFullYear(), kst.getMonth(), kst.getDate()),
          );
        };

        const todayKST = toKSTDateOnly(Date.now());
        const targetKST = toKSTDateOnly(minTsMs);

        const MS_PER_DAY = 24 * 60 * 60 * 1000; // 86,400,000ms
        const daysDelta = Math.round((todayKST - targetKST) / MS_PER_DAY); // 항상 캘린더 일수 차이

        return {
          id: `box-${index}`,
          value: +line.close_box,
          color: boxLineColor(line),
          width: boxLineActive(line) ? 2 : 1,
          dashStyle: boxLineActive(line) ? 'Solid' : 'Dash',
          label: {
            text: `${daysDelta}일전 - ${line.close_box}`,
            align: 'left',
          },
        };
      });

  // 4) 라운드 피겨 박스라인 파트
  const calRoundFigureBoxLines = () =>
    ROUND_FIGURE_BOX_LINES_VALUE.map((item) => {
      return {
        id: `round-${item}`,
        value: +item,
        color: 'black',
        width: 2,
        dashStyle: 'solid',
        label: {
          text: `라운드피겨 (${item})`,
          align: 'left',
        },
      };
    });

  /* plotLines에 대한 useMemo 파트 */
  const allPlotLines = useMemo(() => {
    return [
      // ...calMostPlotLines(),
      ...calTopVolumePlotLines(),
      ...calBoxPlotLines(),
      ...calRoundFigureBoxLines(),
    ];
  }, [
    boxLines,
    isBoxLines,
    highestVolume,
    visiblePlotLines,
    ROUND_FIGURE_BOX_LINES_VALUE,
  ]);

  /* ---------------------------------------------------- */
  /* ----------- { 과거 차트 데이터 로드 기능 파트 } ----------- */
  /* ---------------------------------------------------- */

  // 과거 데이터를 불러와 차트 앞쪽에 prepend
  const loadPrevData = async () => {
    if (!earliestTsRef.current || loadingPrev) return;
    stopPolling();
    setLoadingPrev(true);

    // 최초 범위: 직전 24 h
    let endDate = new Date(earliestTsRef.current - 1_000);
    let startDate = new Date(earliestTsRef.current - 24 * 3600 * 1_000);

    while (true) {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/data/ls_nasdaq_ohlcp/range`,
          {
            params: {
              start: startDate.toISOString(),
              end: endDate.toISOString(),
            },
          },
        );

        if (data.length) {
          const newPoints = data
            .sort(
              (a, b) =>
                new Date(a.timestamp_korean) - new Date(b.timestamp_korean),
            )
            .map((item) => [
              new Date(item.timestamp_korean).getTime(),
              +item.open,
              +item.high,
              +item.low,
              +item.close,
            ]);

          const chart = chartRef.current.chart;
          const priceSeries = chart.series[0];

          priceSeries.setData(
            [...newPoints, ...priceSeries.options.data],
            false,
          );

          chart.xAxis[0].setExtremes(
            newPoints[0][0], // 새로 추가된 맨 앞 timestamp
            chart.xAxis[0].max, // 기존 max 그대로
            false,
            false,
          );

          chart.redraw();

          earliestTsRef.current = newPoints[0][0];
          break;
        } else {
          // 응답이 200인데 배열이 빈 경우 처리
          throw { response: { status: 404 } };
        }
      } catch (err) {
        //데이터가 완전히 없는 날(예: 주말) -> 24 h 추가 확대
        if (err.response?.status === 404) {
          // 더 뒤로 24 h 확장
          startDate = new Date(startDate - 24 * 3600 * 1_000);
          // 안전장치: 7번 이상 시도 시 중단
          if (endDate - startDate > 7 * 24 * 3600 * 1_000) {
            alert('더 이전 구간에도 데이터가 없습니다.');
            break;
          }
          // 다시 while 루프 앞으로
          continue;
        } else {
          console.error(err);
          alert('과거 데이터 로딩 실패');
          break;
        }
      }
    }

    setLoadingPrev(false);
    startPolling();
    loadCountRef.current += 1;
  };

  const startPolling = () => {
    if (pollingTimerRef.current) return; // 이미 돌고 있으면 무시
    pollingTimerRef.current = setInterval(fetchLatest, 5_000); // 5초마다
  };

  const stopPolling = () => {
    if (!pollingTimerRef.current) return;
    clearInterval(pollingTimerRef.current);
    pollingTimerRef.current = null;
  };

  /* ----------------------------------------------- */
  /* -------------- { useEffect 파트 } -------------- */
  /* ------------------------------------------------*/

  // plotLines에 대한 useEffect 파트
  useEffect(() => {
    if (!chartRef.current?.chart) return;
    const yAxis = chartRef.current.chart.yAxis[0];
    if (!yAxis) return;

    yAxis.update({ plotLines: allPlotLines }, false);
    yAxis.chart.redraw(false);
  }, [allPlotLines]);

  // 1) 초기 데이터 로딩: DB의 ls_nasdaq_ohlcp 데이터를 불러와 차트 구성. (timestamp_korean 사용)
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/data/ls_nasdaq_ohlcp/range`, {
        params: {
          start: getKSTIsoString(
            new Date(Date.now() - INITIAL_HOURS_RANGE_FETCH * 60 * 60 * 1000),
          ),
        },
      })
      .then(async (response) => {
        const sorted = response.data.slice().sort((a, b) => {
          return new Date(a.timestamp_korean) - new Date(b.timestamp_korean);
        });

        const priceData = sorted.map((item) => [
          new Date(item.timestamp_korean).getTime(),
          +item.open,
          +item.high,
          +item.low,
          +item.close,
        ]);

        // 마지막, 맨처음 데이터의 timestamp 저장
        if (sorted.length > 0) {
          lastTimestampRef.current = sorted[sorted.length - 1].timestamp_korean;
          firstTimestampRef.current = sorted[0].timestamp_korean;
          earliestTsRef.current = priceData[0][0];
        }

        // 최근 100개 데이터로 xAxis 범위 설정
        // const total = priceData.length;
        // let xAxisOptions = {};
        // if (total > 3600) {
        //   const startTimestamp = priceData[total - 3600][0];
        //   const endTimestamp = priceData[total - 1][0];
        //   xAxisOptions = {
        //     min: startTimestamp,
        //     max: endTimestamp,
        //   };
        // }

        setChartOptions({
          credits: {
            enabled: false,
          },
          chart: {
            height: (8 / 16) * 100 + '%',
            // borderColor: '#EBBA95',
            // borderWidth: 1,
            // borderRadius: 10,
            // spacingRight: 50,
            // redraw 이벤트를 활용하여 plotLines 재렌더링
            zooming: {
              type: 'x',
            },
          },
          palette: {
            colorScheme: 'light',
          },
          time: {
            timezone: 'Asia/Seoul',
          },
          rangeSelector: {
            selected: 2,
            buttons: [
              {
                type: 'minute',
                count: 30,
                text: '30m',
              },
              {
                type: 'hour',
                count: 1,
                text: '1h',
              },
              {
                type: 'hour',
                count: 2,
                text: '2h',
              },
              {
                type: 'hour',
                count: 4,
                text: '4h',
              },
              {
                type: 'hour',
                count: 8,
                text: '8h',
              },
              {
                type: 'day',
                count: 1,
                text: '1D',
              },
              {
                type: 'all',
                text: 'All',
              },
            ],
          },
          tooltip: {
            backgroundColor: '#00ff0000',
            shadow: false,
            split: false,
            shape: 'square',
            headerShape: 'callout',
            shared: true,
            snap: 0,
            positioner: () => ({ x: 60, y: 30 }),
          },
          plotOptions: {
            series: {
              states: {
                hover: {
                  enabled: false,
                },
              },
              lastPrice: {
                enabled: true,
                color: '#00ff0000',
                label: {
                  enabled: true,
                  borderColor: 'grey',
                  borderWidth: 1,
                  backgroundColor: '#00ff0000',
                  format: '{value:.2f}',
                  style: {
                    color: 'black',
                    fontWeight: 'bold',
                    textAlign: 'right',
                  },
                },
              },
            },
          },
          annotations: [],
          responsive: {
            rules: [
              {
                condition: {
                  maxHeight: 800,
                },
              },
            ],
          },
          yAxis: [
            // Main Chart
            {
              title: { text: '가격' },
              height: '100%',
              lineWidth: 2,
              opposite: true,
              labels: {
                align: 'left',
                x: 2,
                format: '{value:.2f}',
              },
              crosshair: {
                label: {
                  enabled: true,
                  format: '{value:.2f}',
                },
                snap: false,
              },
              plotLines: [],
            },
            // Sub Chart
            {
              title: { text: '거래 강도' },
              top: '10%',
              height: '30%',
              // offset: 0,
              lineWidth: 2,
              // 반응형 y축 범위: 초기 기본 범위 (추후 useEffect로 업데이트)
              min: -50,
              max: 50,
              showLastLabel: true,
              crosshair: {
                // label: {
                //   enabled: true,
                //   format: '{value:.0f}',
                // },
                snap: false,
              },
              labels: {
                align: 'left',
                // format: '{value:.0f}',
              },
              plotLines: [],
            },
          ],
          xAxis: [
            {
              overscroll: 200 * 1000,
              gridLineWidth: 1,
              tickInterval: 3600 * 1000, // 1시간 = 3600000 밀리초
              minRange: (10 / 60) * 1 * 3600000, // x축 최소 범위: 10분
              align: 'high',
              type: 'datetime',
              // min: xAxisOptions.min,
              // max: xAxisOptions.max,
            },
            // {
            //   overscroll: 200 * 1000,
            //   gridLineWidth: 1,
            //   tickInterval: 3600 * 1000, // 1시간 = 3600000 밀리초
            //   minRange: (10 / 60) * 1 * 3600000, // x축 최소 범위: 10분
            //   align: 'high',
            //   type: 'datetime',
            //   min: xAxisOptions.min,
            //   max: xAxisOptions.max,
            // },
          ],
          series: [
            {
              type: 'ohlc',
              name: '체결가격',
              data: priceData,
              tooltip: { valueDecimals: 2 },
              lineWidth: 1,
              yAxis: 0,
              color: 'black',
              pointInterval: 1000, // 1초
              dataGrouping: {
                // enabled: false,
                groupPixelWidth: 3,
              },
            },
            {
              type: 'line',
              name: '거래 강도',
              data: [], // 초기에는 빈 배열; START 후 업데이트
              yAxis: 1,
              tooltip: { valueDecimals: 0 },
              color: 'blue',
              pointInterval: 1000, // 1초,
              dataGrouping: {
                // enabled: false,
                groupPixelWidth: 3,
              },
            },
          ],
          stockTools: {
            gui: {
              enabled: true,
              // // 기본 설정(원하는 도구들을 제거하면 오류 발생할 수 있으므로 기본 옵션 사용)
              // // 원하는 도구들만 활성화
              // buttons: [
              //   "simpleShapes",
              //   "lines",
              //   "measure",
              //   "toggleAnnotations",
              //   "fullScreen",
              //   "currentPriceIndicator",
              // ],
              // definitions: {
              //   simpleShapes: {
              //     items: ["rectangle", "circle"],
              //   },
              //   lines: {
              //     items: ["horizontalLine"],
              //   },
              //   measure: {
              //     items: ["measureY"],
              //   },
              //   currentPriceIndicator: {
              //     enabled: true,
              //   },
              // },
            },
          },
        });

        startPolling(); // 초기 데이터가 준비된 뒤 폴링 시작

        // 차트 구간에 맞춰 자동거래 라벨 최초 조회
        if (earliestTsRef.current && lastTimestampRef.current) {
          await fetchTradeLabelsForRange(
            getSessionStartMsKST(-OFFSET_SESSION_DAYS - loadCountRef.current),
            new Date(lastTimestampRef.current).getTime(),
          );
        }
      })
      .catch((error) => {
        console.error('LS NASDAQ OHLCP 데이터 로딩 에러:', error);
      });

    return () => stopPolling(); // 언마운트 시 정리
  }, []);

  // 2) boxLines, tradeLabels 업데이트
  useEffect(() => {
    if (!chartRef.current?.chart) return;
    const chart = chartRef.current.chart;

    // boxLines → annotation 배열 한 방에 생성
    const boxAnnotations = boxLines
      .filter(
        (lineItem) =>
          boxLineTimes(lineItem).length >= 2 && !isNaN(lineItem.close_box),
      )
      .map((lineItem, i) => {
        // 봉우리 객체를 시간순으로 정렬한 뒤 x 좌표 추출
        const sortedTimes = boxLineTimes(lineItem);

        // 정렬된 밀리초 배열로 포인트 객체 생성
        const points = sortedTimes.map((x) => ({
          xAxis: 0,
          yAxis: 0,
          x,
          y: lineItem.close_box,
        }));

        return {
          id: `box-${i}`, // 중요: 고유 id -> oneToOne 매칭
          type: 'crookedLine', // 루트-레벨
          // draggable: '',
          controlPointOptions: { visible: true },
          typeOptions: {
            points,
            line: boxLineStroke(lineItem),
          },
        };
      });

    // 자동거래 라벨(한 개의 annotation 그룹으로 묶음)
    const tradeLabelsAnnotation =
      tradeLabels.length > 0
        ? [
            {
              id: 'trade-labels',
              draggable: 'y',
              labels: tradeLabels,
              // 공통 labelOptions(선택)
              labelOptions: {
                allowOverlap: true, // 겹침 허용(필요 시)
                crop: false,
                overflow: 'justify',
              },
              zIndex: 7,
            },
          ]
        : [];

    // chart.update — oneToOne = true → id가 없는 예전 annotation 은 자동 제거
    chart.update(
      { annotations: [...boxAnnotations, ...tradeLabelsAnnotation] }, // 새 배열 통째로
      false, // redraw 나중에
      true, // oneToOne
    );
    chart.redraw(true);
  }, [boxLines, tradeLabels]);

  // 3) custom 강도 차트 관련

  // 차트 이벤트 바인딩 (차트가 생긴 뒤 1회만)
  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    chart.update({ chart: { events: { click: onChartClick } } }, false);
    chart.redraw(false);
  }, [chartRef.current]);

  // 커서 모양
  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart || !chart.container) return;
    chart.container.style.cursor = customRangeArmed
      ? 'crosshair'
      : customStartArmed
        ? 'crosshair'
        : 'default';
  }, [customRangeArmed, customStartArmed]);

  // ESC 취소 (Range/Start 모두)
  useEffect(() => {
    const onKey = (ev) => {
      if (ev.key === 'Escape') {
        if (customRangeArmedRef.current) {
          setCustomRangeArmed(false);
          setRangeSelectPhase('idle');
          clearRangeMarkers();
        }
        if (customStartArmedRef.current) {
          setCustomStartArmed(false);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // 4) tradeLabels 5초 폴링: 당일 세션 시작~현재(또는 마지막 캔들) 구간 재조회
  useEffect(() => {
    const poll = async () => {
      if (tradeLabelsBusyRef.current) return;
      tradeLabelsBusyRef.current = true;
      try {
        // 차트가 이미 로딩된 경우, 마지막 캔들 시각을 우선 사용
        const endMs = lastTimestampRef.current
          ? new Date(lastTimestampRef.current).getTime()
          : (chartRef.current?.chart?.xAxis?.[0]?.max ?? Date.now());
        const startMs = getSessionStartMsKST(
          -OFFSET_SESSION_DAYS - loadCountRef.current,
        );
        await fetchTradeLabelsForRange(startMs, endMs);
      } catch (e) {
        // 콘솔만 남기고 무시 (다음 폴링에서 재시도)
        console.error('tradeLabels 폴링 에러:', e);
      } finally {
        tradeLabelsBusyRef.current = false;
      }
    };

    // 즉시 1회 실행 + 5초 주기
    poll();
    tradeLabelsPollId.current = setInterval(poll, 5_000);
    return () => {
      if (tradeLabelsPollId.current) {
        clearInterval(tradeLabelsPollId.current);
        tradeLabelsPollId.current = null;
      }
    };
  }, []);

  // 5) Top Volume 선택값을 메인 가격차트 위에 scatter로 오버레이
  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    const seriesId = 'topVolumeScatter';

    // visiblePlotLines에서 meta(timestamp_korean, close, interval, rank, volume, power)가 있는 것만 추출
    const points = [];
    Object.entries(visiblePlotLines || {}).forEach(([key, val]) => {
      if (!isLineChecked(val)) return;
      if (typeof val !== 'object' || val === null) return;

      const { timestamp_korean, close, interval, rank, volume, power } = val;
      if (!timestamp_korean || close == null) return;

      const x = new Date(timestamp_korean).getTime();
      const y = Number(close);
      const v = Number(volume);
      const p = Number(power);
      if (!Number.isFinite(x) || !Number.isFinite(y)) return;

      points.push({
        x,
        y,
        interval: interval || null,
        rank: rank != null ? rank : null,
        volume: v,
        power: p,
      });
    });

    // 기존 scatter 시리즈 찾기
    let scatterSeries = chart.series.find(
      (s) => s.userOptions && s.userOptions.id === seriesId,
    );

    if (!scatterSeries) {
      // 최초 1회 생성
      scatterSeries = chart.addSeries(
        {
          id: seriesId,
          type: 'scatter',
          name: '시간대별 체결량 포인트',
          data: points,
          yAxis: 0, // 메인 가격 차트 위에 오버레이
          zIndex: 5, // 봉보다 위에 보이도록
          marker: {
            symbol: 'circle',
            radius: 5,
          },
          states: {
            inactive: {
              opacity: 1,
              enabled: false,
            },
          },
          dataLabels: {
            enabled: true,
            allowOverlap: true,
            formatter: function () {
              const interval = this.point.interval || '';
              const rank = this.point.rank;
              const v = this.point.volume;
              const p = this.point.power;
              const vTxt =
                typeof v === 'number' && Number.isFinite(v)
                  ? Highcharts.numberFormat(v, 0)
                  : '-';
              const pTxt =
                typeof p === 'number' && Number.isFinite(p)
                  ? Highcharts.numberFormat(p, 0)
                  : '-';
              const rankTxt =
                typeof rank === 'number' || typeof rank === 'string'
                  ? `${rank}위`
                  : '';
              const intervalTxt = interval ? `${interval}` : '';
              // 예시:
              // [1h / 1위]
              // V: 12,345
              // P: 678
              const header =
                intervalTxt || rankTxt
                  ? `[${[intervalTxt, rankTxt].filter(Boolean).join(' / ')}]`
                  : '';

              const line1 = header;
              const line2 = `V:${vTxt}`;
              const line3 = `P:${pTxt}`;

              return [line1, line2, line3].filter(Boolean).join('\n');
            },
            style: {
              fontSize: '11px',
              textOutline: 'none',
            },
          },
          tooltip: {
            valueDecimals: 2,
            pointFormatter: function () {
              const dt = Highcharts.dateFormat('%Y-%m-%d %H:%M:%S', this.x);
              const price = Highcharts.numberFormat(this.y, 2);
              const v =
                typeof this.point.volume === 'number' &&
                Number.isFinite(this.point.volume)
                  ? Highcharts.numberFormat(this.point.volume, 0)
                  : '-';
              const p =
                typeof this.point.power === 'number' &&
                Number.isFinite(this.point.power)
                  ? Highcharts.numberFormat(this.point.power, 0)
                  : '-';
              const interval =
                this.point.interval != null ? String(this.point.interval) : '-';
              const rank =
                this.point.rank != null ? String(this.point.rank) : '-';
              return (
                `발생시각: ${dt}<br/>` +
                `종가: ${price}<br/>` +
                `체결량: ${v}<br/>` +
                `강도: ${p}<br/>` +
                `시간대: ${interval}<br/>` +
                `순위: ${rank}`
              );
            },
          },
        },
        false,
      );
    } else {
      // 이미 있으면 데이터만 교체
      scatterSeries.setData(points, false);
    }

    chart.redraw(false);
  }, [visiblePlotLines]);

  return (
    <div>
      {/* 차트와 버튼들 영역 */}
      <div className='flex flex-wrap justify-center items-end pt-2 pl-2 '>
        {/* 왼쪽: 차트 영역 */}
        <div className='flex-1 min-w-[30rem]'>
          <div className='flex items-center gap-4'>
            <FuturesInfo latestClose={latestClose} />
            <MemoPopup />
            <div className='text-center'>
              <button
                onClick={loadPrevData}
                disabled={loadingPrev || !chartOptions.series}
                className='inline-flex items-center justify-center rounded-md
                         px-3 py-2 text-white bg-sky-600
                         hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500
                         disabled:opacity-60 disabled:cursor-not-allowed'
              >
                {loadingPrev ? '로딩 중…' : '더 불러오기'}
              </button>
            </div>
          </div>
          {chartOptions.series && (
            <HighchartsReact
              highcharts={Highcharts}
              constructorType='stockChart'
              options={chartOptions}
              ref={chartRef}
            />
          )}
        </div>

        {/* 오른쪽: 컨트롤 영역 */}
        {/* flex-none + shrink-0로 찌그러짐 방지, 고정폭(반응형)으로 안정적인 비율 유지 */}
        <div className='flex-none w-[10rem] sm:w-[12rem] md:w-[14rem] lg:w-[16rem] flex flex-col items-center pb-4 my-auto'>
          <CalendarDisplay />

          {/* 강도 표시 카드 */}
          <div
            className='w-full max-w-[11rem] p-4 mt-8 rounded-lg text-white text-center shadow'
            style={{
              backgroundColor: !isPowerRecording
                ? '#eee4ee'
                : isPowerRecording && currentCumulativePower < 0
                  ? '#007BFF'
                  : '#DC3545',
            }}
          >
            <p className='my-1'>최고 강도: {powerMinMax.maxPower}</p>
            <p className='my-1 font-bold text-[2.5rem]'>
              {currentCumulativePower}
            </p>
            <p className='my-1'>최저 강도: {powerMinMax.minPower}</p>
          </div>

          {isPowerRecording ? (
            <p className='text-base font-bold'>강도 기록중</p>
          ) : (
            <p className='text-base font-bold'> - </p>
          )}

          {/* START / RESET 버튼 영역 */}
          <div className='flex gap-4 mt-4'>
            <button
              onClick={handleStartPower}
              disabled={
                !chartRef.current || !chartRef.current.chart || isPowerRecording
              }
              className='px-3 text-[1.4rem] font-semibold rounded-[5px] h-[4.2rem] text-white
              focus:outline-none focus:ring-2 focus:ring-sky-500
              disabled:opacity-60 disabled:cursor-not-allowed'
              style={{
                backgroundColor: isPowerRecording ? '#6c757d' : '#007BFF',
                cursor: 'pointer',
              }}
            >
              START
            </button>
            <button
              onClick={handleResetPower}
              disabled={!chartRef.current || !chartRef.current.chart}
              className='px-3 text-[1.4rem] font-semibold rounded-[5px] h-[4.2rem] text-white
              focus:outline-none focus:ring-2 focus:ring-sky-500
              disabled:opacity-60 disabled:cursor-not-allowed'
              style={{
                backgroundColor: !isPowerRecording ? '#6c757d' : '#DC3545',
                cursor: 'pointer',
              }}
            >
              RESET
            </button>
          </div>
          <div className='flex gap-4 mt-4'>
            {/* Custom START */}
            <button
              onClick={() => {
                setCustomStartArmed((v) => {
                  const armed = !v;
                  if (armed) {
                    // Range 모드 강제 해제 및 마커 정리
                    setCustomRangeArmed(false);
                    setRangeSelectPhase('idle');
                    clearRangeMarkers();
                  }
                  return armed;
                });
              }}
              disabled={!chartRef.current || !chartRef.current.chart}
              className='px-3 text-[1.2rem] font-semibold rounded-[5px] h-[4.2rem] text-white
              focus:outline-none focus:ring-2 focus:ring-sky-500
              disabled:opacity-60 disabled:cursor-not-allowed'
              style={{
                backgroundColor: customStartArmed ? '#6c757d' : '#198754',
                cursor: 'pointer',
              }}
              title='버튼을 누른 뒤 차트에서 시작점을 클릭하세요 (ESC로 취소)'
            >
              {customStartArmed ? '클릭 대기 중(ESC취소)' : 'Custom START'}
            </button>
          </div>
          <div className='flex gap-4 mt-4'>
            {/* Custom START Range */}
            <button
              onClick={() => {
                setCustomRangeArmed((v) => {
                  const armed = !v;
                  if (armed) {
                    // Custom START(단일) 강제 해제
                    setCustomStartArmed(false);
                    clearRangeMarkers();
                    setRangeSelectPhase('start');
                  } else {
                    setRangeSelectPhase('idle');
                    clearRangeMarkers();
                  }
                  return armed;
                });
              }}
              disabled={!chartRef.current || !chartRef.current.chart}
              className='px-3 text-[1.2rem] font-semibold rounded-[5px] h-[4.2rem] text-white
               focus:outline-none focus:ring-2 focus:ring-sky-500
               disabled:opacity-60 disabled:cursor-not-allowed'
              style={{
                backgroundColor: customRangeArmed ? '#6c757d' : '#20c997',
                cursor: 'pointer',
              }}
              title='버튼을 누른 뒤 차트에서 시작점과 끝점을 차례대로 클릭(ESC로 취소)'
            >
              {customRangeArmed
                ? rangeSelectPhase === 'start'
                  ? '구간 시작 클릭(ESC취소)'
                  : '구간 끝 클릭(ESC취소)'
                : 'Custom START Range'}
            </button>
          </div>
        </div>
      </div>
      <div className='flex py-4 items-start justify-center gap-3'>
        <div className='flex-none'>
          <AutoTradingStatsPanel latestClose={latestClose} />
        </div>
        <div className='flex-none'>
          <AutoTradingDetailHistoryPage />
        </div>
      </div>
    </div>
  );
}
